<template>
  <ion-app
    :class="{ 'has-company-picture': companyPictureUrl, 'single-toplevel-category': hasOnlyOneTopLevelCategory, 'has-customer': Boolean(customer) }">


    <!-- screen-reader live region for dynamic announcements (cart updates, search results) -->
    <div class="sr-only" aria-live="polite" role="status" aria-atomic="true" v-text="liveAnnouncement"></div>

    <div class="topbars" ref="topbars" :style="topbarsHidden ? { marginTop: `-${topbarsHeight}px` } : null">
      <header class="company-info" v-if="company">
        <img :src="companyPictureUrl" class="company-picture" v-if="companyPictureUrl" @click="goToStartpage"
          :alt="company ? company.name : ''" />
        <div class="headings" @click="goToStartpage">
          <h1 class="company-name">{{ company.name }}</h1>
          <h2 class="pointofsale-name" v-if="pointofsale"
            v-text="services.translations.translation2Text(pointofsale.name)">
          </h2>
        </div>
        <div class="spacer"></div>
        <div class="actions">
          <oido-button class="search-button" icon="search" v-if="isShopRoute" @click="toggleSearch" ref="searchButton"
            :aria-label="$t('menu-search-button-aria-label')" :aria-expanded="searchOpen ? 'true' : 'false'"
            aria-controls="menu-search-view"></oido-button>
          <fab-translation-selector class="small" horizontal="end" vertical="top"
            side="bottom"></fab-translation-selector>
          <Popper arrow offsetDistance="4" class="customer-popper" v-if="accountSystemEnabled"
            @open:popper="profileOpen = true" @close:popper="onProfilePopperClose">
            <oido-button class="profile-button" icon="person-circle" ref="profileButton"
              :aria-label="$t('customer-account-button-aria-label')" aria-haspopup="dialog"
              :aria-expanded="profileOpen ? 'true' : 'false'"></oido-button>
            <template #content="{ close }">
              <CustomerView :close="close"></CustomerView>
            </template>
          </Popper>

        </div>
      </header>

      <div class="search-view" id="menu-search-view" role="search" v-if="company && isShopRoute && searchOpen">
        <ion-searchbar ref="searchbar" mode="ios" :placeholder="$t('menu-search-placeholder')" v-model="searchText"
          :aria-label="$t('menu-search-field-aria-label')" debounce="100" show-clear-button="always"
          @keydown.escape="closeSearch"></ion-searchbar>
        <oido-button class="search-close" icon="close" @click="closeSearch"
          :aria-label="$t('menu-search-close-aria-label')"></oido-button>
      </div>


      <tabs-view v-if="!loading && company && visibleCategories" :tabs="tabs"></tabs-view>
      <div class="filter-view" v-if="!loading && company && company.tags && company.tags.length > 0 && isShopRoute">
        <span class="tag" v-for="tag of company.tags" :key="tag.id" :class="{ active: activeTags.includes(tag.id) }"
          @click="toggleTag(tag)" v-text="services.translations.translation2Text(tag.name)"></span>
      </div>
    </div>
    <!-- plain positioned wrapper (NOT ion-content): ion-content auto-sets role="main", and each
         routed page already has its own ion-content, so wrapping the outlet in one produced a
         second, redundant "main" landmark. This div mirrors ion-content's layout box without it. -->
    <div id="main" class="main-content">
      <ion-router-outlet :animated="false"></ion-router-outlet>
    </div>

    <contact-panel v-if="company"></contact-panel>

    <closed-notice-panel
      v-if="(company && !company.isOpen) || (pointofsale && pointofsale.closed)"></closed-notice-panel>
    <shopping-cart-panel v-if="company && company.isOpen && pointofsale && !pointofsale.closed"></shopping-cart-panel>
  </ion-app>
</template>


<script setup>
import Popper from "vue3-popper"



import {
  IonApp,
  IonRouterOutlet,
  IonContent,
  IonSearchbar,

} from "@ionic/vue"

import { } from "ionicons/icons"
import getCustomDomain from './helpers/getCustomDomain'
import OidoButton from './components/OidoButton.vue'
import FabTranslationSelector from "./components/FabTranslationSelector.vue"
import TabsView from "./components/TabsView.vue"
import config from "./../config"
import ShoppingCartPanel from "./views/ShoppingCartPanel.vue"
import ClosedNoticePanel from "./views/ClosedNoticePanel.vue"
import ContactPanel from "./views/ContactPanel.vue"
import LegalModal from "./views/LegalModal.vue"
import RegistrationModal from "./views/RegistrationModal.vue"
import { modalController } from "@ionic/vue"
import CustomerView from "./views/CustomerView.vue"
import customerService from "./composables/customerService";

</script>
<script>


export default {
  data() {
    return {
      topbarsHeight: 0,
      //tracks the account popover's open state so the trigger can expose aria-expanded
      profileOpen: false,
    }
  },
  computed: {
    topbarsHidden() {
      return this.services.topbar.topbarsHidden.value
    },
    liveAnnouncement() {
      return this.services.announcer.message.value
    },
    cartItemCount() {
      return this.services.cartService.totalNumberOfItems()
    },
    loading() {
      return !this.company || !this.company.id
    },
    selectedLanguage() {
      return this.services.translations.selectedLanguage ? this.services.translations.selectedLanguage.value : null
    },
    activeTags() {
      return this.services.cartService.activeTags ? this.services.cartService.activeTags.value : []
    },
    companyPictureUrl() {
      return this.services.company.companyPictureUrl ? this.services.company.companyPictureUrl.value : null
    },
    slug() {
      return this.$route.params["companyId"] ? this.$route.params["companyId"] : null
    },
    customDomain() {
      return getCustomDomain()
    },
    companySalt() {
      return this.$route.params["companySalt"] ? this.$route.params["companySalt"] : null
    },
    categoryId() {
      return this.$route.params["categoryId"] ? this.$route.params["categoryId"] : null
    },
    pointofsaleId() {
      return this.$route.params["pointofsaleId"] ? this.$route.params["pointofsaleId"] : null
    },
    isShopRoute() {
      return Boolean(this.$route.name == 'menu' || this.$route.name == 'menu-cart')
    },
    searchOpen() {
      return this.services.search.searchOpen.value
    },
    searchText: {
      get() {
        return this.services.search.searchText.value
      },
      set(value) {
        this.services.search.searchText.value = value
      }
    },
    accountSystemEnabled() {
      return Boolean(this.company && this.company.enableAccountSystem)
    },
    hasOnlyOneTopLevelCategory() {
      return this.visibleCategories && this.visibleCategories.length == 1
    },
    visiblePages() {
      let visiblePages = []


      let visibilityRule = 'all'
      let visibilityConfig = null

      //zonewide settings
      //always set rule and Config simultaneously
      if (this.pointofsale && this.pointofsale.pagesVisibilityRule) {
        visibilityRule = this.pointofsale.pagesVisibilityRule
        visibilityConfig = this.pointofsale.pagesVisibilityConfig
      }

      //if a customer is logged in and has its own settings
      if (this.customer && this.customer.pagesVisibilityRule) {
        visibilityRule = this.customer.pagesVisibilityRule
        visibilityConfig = this.customer.pagesVisibilityConfig
      }


      if (this.company && this.company.pages && this.company.pages.length > 0) {
        for (let page of this.company.pages) {
          if (!page.showInMenu) {
            continue
          }

          if (visibilityRule == 'except') {
            let pagesToIgnore = visibilityConfig && visibilityConfig.length > 0 ? visibilityConfig : []
            if (pagesToIgnore.includes(page.id)) {
              continue
            }
          }

          if (visibilityRule == 'only') {
            let pagesToInclude = visibilityConfig && visibilityConfig.length > 0 ? visibilityConfig : []
            if (!pagesToInclude.includes(page.id)) {
              continue
            }
          }
          visiblePages.push(page)
        }
      }

      return visiblePages
    },
    tabs() {
      let tabs = []
      let allParams = { ...this.$route.params }

      delete allParams.categoryId

      if (this.visibleStories && this.visibleStories.length > 0) {
        tabs.push({
          label: this.$t("menu-stories-navigation-label"),
          to: { name: "stories-page", params: allParams },
          class: { active: this.$route.name == 'stories' || this.$route.name == 'story' },
          key: "stories",
        })
      }


      //if there is only one top level category we would end up with two nearly identical Menuitems (All, <<categoryname>>)
      //because the route to the all items page is used elsewhere, we rename the all-products item and hide the category

      let nameOfEverythingButton = this.hasOnlyOneTopLevelCategory ? this.services.translations.translation2Text(this.visibleCategories[0].name) : this.$t("menu-category-navigation-show-all")

      tabs.push({
        label: nameOfEverythingButton,
        to: { name: "menu", params: allParams },
        class: { active: this.$route.name == 'menu' && !this.categoryId, parent: this.categoryId ? true : false },
        key: "everything",
      })

      if (!this.hasOnlyOneTopLevelCategory) {
        for (let category of this.visibleCategories) {
          tabs.push({
            label: this.services.translations.translation2Text(category.name),
            to: { name: "menu", params: { ...this.$route.params, categoryId: category.id } },
            key: typeof category.id !== "undefined" ? category.id : category,
          })
        }
      }

      for (let page of this.visiblePages) {
        tabs.push({
          label: this.services.translations.translation2Text(page.title),
          to: { name: "content-page", params: { ...this.$route.params, pageId: page.id } },
          class: { active: this.$route.name == 'content-page' && this.$route.params.id == page.id },
          key: page.id,
        })
      }

      return tabs
    },
  },
  async unmounted() { },
  watch: {
    company() {

      if (this.company && this.company.id && this.company.enableAccountSystem) {
        customerService.loadLoggedInCustomer(this.company.id)
      }

      //if its a very old url which uses the companyId instead of the slug
      if (this.company && this.$route.params["companyId"] == this.company.id && this.company.slug != this.$route.params["companyId"]) {
        let posId = this.$route.params["pointofsaleId"] ? this.$route.params["pointofsaleId"] : this.company.defaultPointofsaleId
        this.$router.push({
          name: ('' + this.$route.name).replace("-old", ""),
          params: { ...this.$route.params, pointofsaleId: posId, companyId: this.company.slug },
          query: this.$route.query
        })
      } else if (!this.$route.params['pointofsaleId'] && this.company && this.company.defaultPointofsaleId) {
        // it has no pos set so use the default pos
        this.$router.push({
          name: this.$route.name == "default-menu" ? "menu" : this.$route.name,
          params: { ...this.$route.params, pointofsaleId: this.company.defaultPointofsaleId },
          query: this.$route.query
        })
      }

      this.updateWindowTitle()

      if (this.company && (this.$route.name == 'menu' || this.$route.name == 'default-menu')) {
        this.services.statistics.countScan()
      }

      console.log('this.company', this.company)
      if (this.company && this.company.enableLegalAgePopup) {
        this.showLegalPopup()
      }
    },
    registrationRequest() {
      if (this.registrationRequest) {
        this.showRegistrationPopup()
      }
    },
    slug() {
      this.loadCompany()
    },
    pointofsaleId() {
      this.services.company.pointofsaleId.value = this.pointofsaleId
    },
    companySalt() {
      this.loadCompany()
    },
    loading() {
      this.removeLoadingPanel()
    },
    selectedLanguage(selectedLanguage) {
      this.$i18n.locale = selectedLanguage
      this.syncDocumentLanguage(selectedLanguage)
    },
    cartItemCount(count, previousCount) {
      //announce cart size changes to screen readers (the visual badge / toast aren't announced).
      //skip the initial value so we only announce real user-driven changes
      if (typeof previousCount === "number") {
        this.services.announcer.announce(this.$t("cart-updated-announcement", { number: count }))
      }
    },
    $route() {
      this.updateWindowTitle()
      //new view starts scrolled to the top, so reveal the topbars again
      this.services.topbar.reset()
      //leaving the shop view: drop any active search so it doesn't linger
      if (!this.isShopRoute && this.searchOpen) {
        this.services.search.close()
      }
    },
    topbarsHidden(hidden) {
      //capture the natural height right before collapsing so the negative
      //margin-top matches and the slide animates from a known value
      if (hidden && this.$refs.topbars) {
        this.topbarsHeight = this.$refs.topbars.offsetHeight
      }
    },
    searchOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          let searchbar = this.$refs.searchbar
          if (searchbar && searchbar.$el && searchbar.$el.setFocus) {
            searchbar.$el.setFocus()
          }
        })
      } else {
        //the search field is removed from the DOM on close — move focus back to the
        //toggle button so keyboard users don't get dropped onto <body>
        this.$nextTick(() => {
          let button = this.$refs.searchButton
          if (button && button.$el && button.$el.focus) {
            button.$el.focus()
          }
        })
      }
    }
  },
  async mounted() {
    this.updateWindowTitle()
    this.syncDocumentLanguage(this.selectedLanguage)
    this.loadCompany()
    //let the topbar service measure how much height collapsing the bars frees,
    //so it can avoid hiding them when that would leave the content unscrollable
    this.services.topbar.setTopbarElement(this.$refs.topbars)
  },
  methods: {
    onProfilePopperClose() {
      this.profileOpen = false
      //if closing the account popover left focus stranded on <body> (e.g. closed via Escape
      //while focus was inside it), send focus back to the trigger; if the user moved focus
      //elsewhere (click-away), leave it where they put it
      this.$nextTick(() => {
        let active = document.activeElement
        if (!active || active === document.body) {
          let button = this.$refs.profileButton
          if (button && button.$el && button.$el.focus) {
            button.$el.focus()
          }
        }
      })
    },
    syncDocumentLanguage(language) {
      //keep <html lang> in sync with the chosen language so screen readers use the
      //correct pronunciation across the supported locales
      if (language) {
        document.documentElement.lang = language
      }
    },
    updateWindowTitle() {
      let title = "Oido"
      let postfix = null
      // Add the Company Name as Prefix
      if (this.company && this.company.name) {
        title = this.company.name

        if (this.$route.name == "menu-cart") {
          let stepTitles = {
            'cart-details': 'step-flow-cart-details-title',
            'cart-payment': 'step-flow-cart-payment-title',
            'cart-summary': 'shopping-cart-step-summary-title',
            'cart-success': 'shopping-cart-step-success-title',
            'cart-overview': 'shopping-cart-step-overview-title' // shopping-cart-step-whishlist-title
          }
          if (this.$route.params['step'] && stepTitles[this.$route.params['step']]) {
            postfix = this.$t(stepTitles[this.$route.params['step']])
          }
        }
      }


      if (postfix) {
        title += ' - ' + postfix
      }
      document.title = title

    },
    goToStartpage() {
      this.$router.push({
        name: "menu",
        params: { companyId: this.$route.params.companyId, pointofsaleId: this.$route.params.pointofsaleId }
      })
      window.location.reload()
    },
    async showLegalPopup() {
      if (!this.company || !this.company.enableLegalAgePopup) {
        console.error('showLegalPopup no company or not enabled', this.company)
        return
      }
      if (!this.checkoutIsEnabled) {
        console.error('showLegalPopup no pointofsale or checkpout not enabled', this.company)
        return
      }
      let localStorageKey = this.services.cartService.localStorageKey + '-legal-popup'

      let savedState = JSON.parse(localStorage.getItem(localStorageKey))

      if (savedState && savedState.accepted && savedState.date) {
        let secondsSinceLastTime = ((new Date()).getTime() - new Date(savedState.date).getTime()) / 1000
        if (secondsSinceLastTime && secondsSinceLastTime < 60 * 60 * 24 * 5) {
          localStorage.setItem(localStorageKey, JSON.stringify({
            accepted: true,
            date: new Date()
          }))
          return
        } else {
          localStorage.removeItem(localStorageKey)
        }
      }

      this.pointofsale.enableCheckout = false

      const modal = await modalController.create({
        component: LegalModal,
        canDismiss: (value) => {
          return typeof value != 'undefined'
        },
        cssClass: "legal-modal",
        htmlAttributes: { "aria-label": this.$t("legal-modal-aria-label") },
        componentProps: {},
      })
      await modal.present()
      const { data } = await modal.onDidDismiss()
      if (data) {
        this.pointofsale.enableCheckout = true
        localStorage.setItem(localStorageKey, JSON.stringify({
          accepted: true,
          date: new Date()
        }))
      } else {
        this.pointofsale.enableCheckout = false
      }
    },
    async showRegistrationPopup() {
      if (!this.registrationRequest) {
        console.error('showRegistrationPopup:  no registrationRequest', this.company)
        return
      }
      const modal = await modalController.create({
        component: RegistrationModal,
        canDismiss: (value) => {
          return true
        },
        htmlAttributes: { "aria-label": this.$t("registration-modal-headline") },
        componentProps: {},
      })
      await modal.present()
      const { data } = await modal.onDidDismiss()
      if (data) {

      } else {
        customerService.cancelRegistration()
      }
    },
    async loadCompany() {

      let companyId = null

      if (this.customDomain) {
        companyId = await this.companyService.checkCustomDomain(this.customDomain)
      }

      if (!companyId && this.slug) {
        companyId = await this.companyService.checkCompanySlug(this.slug)
      }

      this.companyService.loadCompany(companyId, this.pointofsaleId)
    },

    toggleSearch() {
      this.services.search.toggle()
    },
    closeSearch() {
      this.services.search.close()
    },
    toggleTag(tag) {
      if (this.activeTags.includes(tag.id)) {
        this.activeTags.splice(this.activeTags.indexOf(tag.id), 1)
      } else {
        this.activeTags.push(tag.id)
      }
    },
    removeLoadingPanel() {
      if (!this.loading) {
        // document.body.classList.remove("loading");
        setTimeout(() => {
          document.body.classList.remove("loading")
        }, 1000)
      }
    }
  },
}
</script>

<style>
@import "vue-select/dist/vue-select.css";
body{
  color: black;
}

#app {
  height: 100%;
}

/* visually hidden but available to screen readers (live regions, off-screen labels) */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}


h1,
h2,
h3 {
  font-family: var(--headline-font-family) !important;
}
</style>
<style scoped>
ion-app {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* replaces the outer ion-content that used to wrap the router outlet — same layout box
   (fills the remaining column height, positioned container for the absolute router outlet)
   but without ion-content's automatic role="main" */
.main-content {
  display: block;
  position: relative;
  flex: 1;
  contain: size style;
}

.menu-page {
  padding-bottom: 3rem;
}

.topbars {
  position: relative;
  z-index: 10;
  flex: none;
  background-color: var(--ion-background-color, #fff);
  transition: margin-top 0.28s ease;
  will-change: margin-top;
}


.company-info {
  display: flex;
  border-bottom: 1px solid #ddd;
  padding: 1rem 1.5rem;
  padding-bottom: 0.6rem;
  min-height: auto;
  align-items: center;  
  color: #1e1e1e;
  cursor: pointer;
  justify-content: space-between;
}


.company-info .divider {
  display: none;
}

.company-info .company-picture {
  width: 50px;
  height: 50px;
  margin-bottom: 0px;
  margin-right: 15px;
  object-fit: cover;
  border-radius: 50%;
}


.company-info .company-name {
  margin-top: 5px;
  margin-bottom: 0;
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: capitalize;
}

.company-info .pointofsale-name {
  font-size: 1rem;
  margin: 0.2rem 0;
  font-weight: 500;
}

.company-info .spacer {
    flex: 1;
  }

  
.company-info .actions {
  display: flex;
  align-items: center;
}

.loading-indicator {
  height: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.loading-indicator ion-spinner {
  width: 100px !important;
  height: 90px !important;
}

.loading-indicator .label {
  margin-top: 1rem;
  color: var(--ion-color-medium);
  font-size: 0.8rem;
  display: none;
}

.filter-view {
  background-color: var(--ion-color-light);
  color: var(--ion-color-light-contrast);
  border-bottom: 1px solid var(--ion-color-light-shade);
  padding: 0rem 1rem;
  font-size: 0.8rem;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}

.search-view {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--ion-color-light-shade);
  padding: 0.2rem 1rem;
}

.search-view ion-searchbar {
  flex: 1;
  padding: 0;
  --box-shadow: none;
  --background: var(--ion-color-light);
}

.search-view .search-close {
  margin-left: 0.5rem;
  font-size: 1.1rem;
}

.filter-view .tag {
  text-transform: capitalize;

  padding: 0.3rem 0.5rem;
  cursor: pointer;
  height: 100%;
  display: flex;
  align-items: center;
}

.filter-view .tag.active {
  color: var(--ion-color-primary);
  background-color: rgba(0, 0, 0, 0.03);
  border-right: 1px solid #cecece;
  border-left: 1px solid #cecece;
}

.filter-view .tag.active+.tag.active {
  border-left: none;
}
</style>


<style>
body {

  color: var(--ion-color-dark);
}


textarea {
  background-color: #fff !important;
}

.validation-errors.global {
  padding-left: 16px;
}

.validation-error {
  color: var(--ion-color-danger);
  font-size: 0.8rem;
  padding-top: 0.5rem !important;
  margin-bottom: -.3rem;
  display: block;
}

.currency-symbol {
  padding-left: .15rem;
}


.warning-sign {
  margin: 1rem 2rem;
  padding: 1rem;
  text-align: center;
  border-radius: 5px;
  color: white;
  font-weight: bold;
  background: #db22418a;
  background: var(--ion-color-primary);
  position: relative;
  text-align: center;
  z-index: 200;
  line-height: 1.5;
}

.warning-sign a {
  z-index: 200;
  position: relative;
  color: white
}

@media (min-width: 700px) {
  .warning-sign {
    margin: 1rem auto;
    max-width: 600px;
  }
}



@media (max-width: 700px) {

  ion-toast {
    top: -10px !important;
    left: -10px !important;
    width: calc(100vw + 20px) !important;
    border-radius: 0;
    max-width: auto !important;
  }
}


.rtf-note {
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1.3;
}

.rtf-note h1 {
  font-size: 1.4rem;
  font-weight: 600;
  margin-top: 0.3rem;
  margin-bottom: 0.4rem
}

.rtf-note h2 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 0.3rem;
  margin-bottom: 0.6rem
}

.rtf-note h3 {
  font-size: 0.9rem;
  font-weight: 590;
  margin-top: 0.2rem;
  margin-bottom: 0.6rem
}

.rtf-note p {
  margin-top: 0.3rem;
  margin-bottom: 0.6rem;
}


@media (min-width: 900px) {

  .frontpage-notice.rtf-note {
    text-align: left !important;
  }

}

ion-modal::part(content) {
  background-color: transparent
}

ion-modal::part(backdrop) {
  background-color: rgba(0, 0, 0, 0.75);
}
</style>



<style>
@media (max-width: 900px) {

  .related-stories {
    margin-top: 7rem;
  }

  .related-stories .story-list-item h1.title {
    font-size: 1rem;
  }

  /* .related-stories .story-list-item .list-item-content .meta,
  .related-stories .story-list-item {
    font-size: 0.8rem;
  } */

  .related-stories .story-list-item:first-child {
    padding-top: 1rem;
    border-top: 1px solid var(--ion-color-light-shade);
  }


}

@media (min-width: 900px) {

  .related-stories {
    width: 25%;
  }


  .related-stories .story-list-item h1.title {
    font-size: 0.8rem;
  }

  .related-stories .story-list-item .list-item-content .meta,
  .related-stories .story-list-item {
    font-size: 0.7rem;
  }

  .related-stories .story-list-item .list-item-before {
    padding-right: 0.7rem;
  }

  .related-stories .story-list-item:first-child {
    margin-top: 0;
    padding-top: 0rem;
  }

  .related-stories .story-list-item .picture {
    width: 40%;
    max-width: none
  }

  .related-stories .story-list-item .intro {
    display: none !important;
  }

}


.text-content {
  line-height: 1.4;
}

.text-content>p:first-child {
  margin-top: 0;
  padding-top: 0;
}

.text-content img {
  width: 65%;
  max-width: 400px;
  margin: 2rem auto;
  display: block;
}

@media (max-width: 700px) {
  .text-content img {
    width: 100%;
  }

}

.text-content .quote {
  padding: 2rem;
}

.text-content .quote blockquote {
  font-weight: 200;
  font-size: 1.15rem;
  text-align: center;
  font-style: italic;
  margin: 0;
}

.text-content .quote .caption {
  font-weight: 300;
  font-size: 1.15rem;
  text-align: center;
  margin-top: 1rem;
}


.single-toplevel-category .product-list-category-item.level-1>.category-name {
  display: none;
}

.single-toplevel-category .product-list-category-item.level-1>.children:first-of-type>.product-list-category-item.level-2:first-child>h2 {
  margin-top: 0;
}

.text-content .link-container {
  padding: 1rem;
  margin: 1rem 0;
  box-shadow: 0px 1px 15px hsla(0, 0%, 39%, 0.15);
  border-radius: 8px;
}

.text-content .link-container h3.title {
  margin-top: 0.3rem;
  font-size: 1rem;
}

.text-content .link-container .description {
  margin-top: 0.3rem;
  margin-bottom: 0.8rem;
  font-size: 0.8rem;
}


.actions .search-button {
  font-size: 1.9rem;
  margin-right: 0.6rem;

  margin-left: 0.3rem;
}

.actions .search-button:hover svg {
  stroke: var(--ion-color-primary);
}

.actions .fab-translation-selector {
  display: block;
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  bottom: auto;
  margin-right: 0.8rem;
  margin-bottom: -0.1rem;

}


/* ion-app:not(.has-company-picture) .fab-translation-selector {
  margin-top: -0.13rem;
}

ion-app:has(.customer-popper) .fab-translation-selector {
  margin-right: 3rem;
}

@media (max-width: 620px) {
  ion-app:not(.has-company-picture) .fab-translation-selector {
    margin-top: -0.1rem;
  }
} */

.fab-translation-selector>ion-fab-button::part(native) {
  box-shadow: none;
}

.fab-translation-selector.small>ion-fab-button::part(native) {}

.fab-translation-selector.small ion-fab-button,
.fab-translation-selector.small ion-fab-button::part(native) {
  width: 30px;
  height: 30px;
}

.fab-translation-selector.small ion-fab-list {
  margin-top: 40px;
  min-width: 33px !important;
  margin-left: 6px;
}


.profile-button .icon {
  font-size: 2.15rem;
}

.profile-button:hover svg {
  fill: var(--ion-color-primary);
}


.actions .popper {
  margin-left: -6px !important;
}


.has-customer .profile-button svg {
  fill: var(--ion-color-primary);

}

.actions .popper {
  min-width: 300px;
}


.customer-popper {
  border: none !important;
  margin-top: -6px;

}
</style>



<style>
:root {
  --popper-theme-background-color: #fff;
  --popper-theme-background-color-hover: #fff;
  --popper-theme-text-color: #333;
  --popper-theme-border-width: 0px;
  --popper-theme-border-style: solid;
  --popper-theme-border-radius: 6px;
  --popper-theme-padding: 1rem;
  --popper-theme-box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.45);
}
</style>

<style>
ion-checkbox::part(label) {
  text-wrap: balance;
}
</style>